import React, { useState, useEffect, useCallback } from "react";
import { hot } from "react-hot-loader/root";
import { history } from "./stores/configureStore";
import { Route, Switch } from "react-router-dom";
import { ConnectedRouter } from "connected-react-router";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { ConfigProvider, Layout, Row, BackTop, message } from "antd";
import ReactGA from "react-ga";
import * as moment from "moment";
import * as actions from "./actions";
import PrivateRoute from "./components/privateroute";
import Nav from "./components/nav";
import Home from "./pages/home";
import Details from "./pages/details";
import DealerPortalAccess from "./pages/dealerportalaccess";
import LoginPage from "./pages/login";
import ForgotPasswordPage from "./pages/forgotpassword";
// import { useWhyDidYouUpdate } from "./hooks/useWhyDidYouUpdate";

import { AuthState, User } from "./reducers/auth";
import { LanguageState } from "./reducers/language";
import { FiltersState } from "./reducers/filters";
import { ItemsState } from "./reducers/items";
import { ShareState } from "./reducers/share";
import { State } from "./interfaces";

const { Content } = Layout;

const App = () => {
  const auth: AuthState = useSelector(
    (state: State) => state.auth,
    shallowEqual
  );
  const {
    user,
    hasError: loginHasError,
    errorMessage: loginError,
    forgotPasswordMessage
  } = auth;

  const filters: FiltersState = useSelector(
    (state: State) => state.filters,
    shallowEqual
  );
  const { hasError: filtersHasError, errorMessage: filtersError } = filters;

  const _language: LanguageState = useSelector(
    (state: State) => state.language,
    shallowEqual
  );
  const {
    labels,
    selectedLanguage,
    hasError: languageHasError,
    errorMessage: languageError
  } = _language;

  const items: ItemsState = useSelector(
    (state: State) => state.items,
    shallowEqual
  );
  const { hasError: itemsHasError, errorMessage: itemsError } = items;

  const share: ShareState = useSelector(
    (state: State) => state.share,
    shallowEqual
  );

  const { hasError: shareHasError, errorMessage: shareError } = share;

  const dispatch = useDispatch();

  const onForgotPasswordSuccess = useCallback(
    path => dispatch(actions.redirectToPage(path)),
    [dispatch]
  );

  const [locale, setLocale] = useState(undefined);
  const [localeChanged, setLocaleChanged] = useState(false);
  const [language, setLanguage] = useState("");

  // useWhyDidYouUpdate("App", props);

  useEffect(() => {
    // ReactGA
    // avvio analytics per questa pagina
    ReactGA.pageview(history.location.pathname);
    // e tutte le pagine successive
    history.listen((location, action) => {
      // ReactGA.set({ page: location.pathname });
      ReactGA.pageview(location.pathname);
      // console.log("location", location.pathname);
    });
  }, []);

  useEffect(() => {
    // componentDidUpdate
    // console.log("componentDidUpdate");

    if (loginHasError) {
      message.error(loginError);
      refreshXCsrfToken(loginError);
    }
    if (filtersHasError) {
      message.error(filtersError);
      refreshXCsrfToken(filtersError);
    }
    if (itemsHasError) {
      message.error(itemsError);
      refreshXCsrfToken(itemsError);
    }
    if (languageHasError) {
      message.error(languageError);
      refreshXCsrfToken(languageError);
    }
    if (shareHasError) {
      message.error(shareError);
      refreshXCsrfToken(shareError);
    }
    if (forgotPasswordMessage && labels) {
      message.success(labels.recoverPasswordSuccessMessage, 2, () =>
        onForgotPasswordSuccess("/")
      );
    }
  }, [
    filtersError,
    filtersHasError,
    forgotPasswordMessage,
    itemsError,
    itemsHasError,
    labels,
    languageError,
    languageHasError,
    loginError,
    loginHasError,
    onForgotPasswordSuccess,
    shareError,
    shareHasError
  ]);

  useEffect(() => {
    // se cambia la lingua importo il nuovo locale
    if (selectedLanguage && selectedLanguage.id !== language) {
      _loadAsyncData(selectedLanguage.id);
    }
  }, [language, selectedLanguage]); // getDerivedStateFromProps

  // se il locale è cambiato devo settare joyride con i testi in lingua
  if (labels && localeChanged) {
    // console.log("joyride con i testi in lingua");
    // Retrieve
    const hideHelp = localStorage.getItem("hideHelp");
    // console.log("hideHelp ", hideHelp);
    if (hideHelp !== "true") {
      setLocaleChanged(false);
    }
  }

  const refreshXCsrfToken = (_message: string | null) => {
    if (_message && _message.indexOf("X-CSRF-Token") !== -1) {
      console.log(_message);
    }
  };

  const _loadAsyncData = async (id: string) => {
    // console.log("_loadAsyncData id ", id);
    let results = [null, null];
    switch (id) {
      case "it":
        results = await Promise.all([
          import("antd/lib/locale-provider/it_IT"),
          import("moment/locale/it")
        ]);
        break;

      case "es":
        results = await Promise.all([
          import("antd/lib/locale-provider/es_ES"),
          import("moment/locale/es")
        ]);
        break;

      case "fr":
        results = await Promise.all([
          import("antd/lib/locale-provider/fr_FR"),
          import("moment/locale/fr")
        ]);
        break;

      case "de":
        results = await Promise.all([
          import("antd/lib/locale-provider/de_DE"),
          import("moment/locale/de")
        ]);
        break;

      default:
        results = [null, null];
    }
    // destrutturo i risultati
    const [antdLocale] = results;

    moment.locale(id);
    setLocale(
      antdLocale !== undefined ? (antdLocale as any)!.default : undefined
    );
    setLocaleChanged(true);
    setLanguage(id);
  };

  return (
    <ConfigProvider locale={locale}>
      <ConnectedRouter history={history}>
        <Layout
          className={user ? "" : "not-logged-in"}
          style={{ minHeight: "100vh" }}
        >
          <Nav />
          <BackTop />
          <Content className="main-content">
            <Switch>
              <Route exact path="/" component={LoginPage} />
              <Route
                exact
                path="/forgot-password"
                component={ForgotPasswordPage}
              />
              <PrivateRoute
                path="/(home|details)"
                component={Home}
                user={user as User}
              />
              <Route
                exact
                path="/dealer/:access_token/:refresh_token"
                component={DealerPortalAccess}
              />
              <Route component={NoMatch} />
            </Switch>
            <PrivateRoute
              path="/details/:itemId"
              component={Details}
              user={user as User}
            />
          </Content>
          {/* <Footer style={{ textAlign: "center" }}>
            © 2017 CNH Industrial America LLC. Tutti i diritti riservati CASE e
            CNH Capital sono marchi registrati di CNH Industrial America LLC.
          </Footer> */}
        </Layout>
      </ConnectedRouter>
    </ConfigProvider>
  );
};

const NoMatch = ({ location }: any) => (
  <Row align="middle" justify="center">
    <h3>
      No match for <code>{location.pathname}</code>
    </h3>
  </Row>
);

export default process.env.NODE_ENV === "development" ? hot(App) : App;
